import React, { useState } from 'react'
import { X, ExternalLink } from 'lucide-react'

function FeedbackModal({ onClose }) {
  const [formData, setFormData] = useState({
    type: 'error',
    organisation: '',
    conference: '',
    description: '',
    correction: '',
    source: '',
    email: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // For MVP, we'll create a GitHub issue URL with pre-filled content
      const issueTitle = `[${formData.type}] ${formData.organisation || 'Data Issue'}`
      const issueBody = `
**Issue Type:** ${formData.type}

**Organisation:** ${formData.organisation || 'N/A'}

**Conference:** ${formData.conference || 'N/A'}

**Description:**
${formData.description}

**Correct Information:**
${formData.correction || 'N/A'}

**Source/Evidence:**
${formData.source || 'N/A'}

**Contact Email:** ${formData.email || 'N/A'}

---
*This issue was automatically generated from the feedback form.*
      `.trim()

      const githubUrl = `https://github.com/your-username/llm-industry-calendar/issues/new?title=${encodeURIComponent(issueTitle)}&body=${encodeURIComponent(issueBody)}`
      
      // Open GitHub issue in new tab
      window.open(githubUrl, '_blank')
      
      setSubmitted(true)
      
      // Close modal after short delay
      setTimeout(() => {
        onClose()
      }, 2000)

    } catch (error) {
      console.error('Error submitting feedback:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="modal-overlay feedback">
        <div className="modal">
          <div className="modal-success-content">
            <h2>Thank you!</h2>
            <p>Your feedback has been submitted. A GitHub issue will open in a new tab.</p>
            <p className="modal-success-subtitle">
              This modal will close automatically...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay feedback" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-simple">
          <h2>Report Issue or Provide Feedback</h2>
          <button 
            onClick={onClose}
            className="modal-close-button"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="type">Issue Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="error">Incorrect Information</option>
              <option value="missing">Missing Information</option>
              <option value="outdated">Outdated Information</option>
              <option value="suggestion">Suggestion</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="organisation">Organisation Name</label>
            <input
              type="text"
              id="organisation"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              placeholder="e.g., New Zealand Institute of Transportation Engineers"
            />
          </div>

          <div className="form-group">
            <label htmlFor="conference">Conference Name (if applicable)</label>
            <input
              type="text"
              id="conference"
              name="conference"
              value={formData.conference}
              onChange={handleChange}
              placeholder="e.g., Annual Transport Conference 2025"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description of Issue</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Please describe what is incorrect, missing, or needs to be updated..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="correction">Correct Information</label>
            <textarea
              id="correction"
              name="correction"
              value={formData.correction}
              onChange={handleChange}
              placeholder="Provide the correct information if you know it..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="source">Source/Evidence URL</label>
            <input
              type="url"
              id="source"
              name="source"
              value={formData.source}
              onChange={handleChange}
              placeholder="https://example.com/evidence"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Your Email (optional)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="For follow-up questions (optional)"
            />
          </div>

          <div className="button-group">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="modal-info-section">
          <div className="modal-info-header">
            <ExternalLink size={16} />
            <strong>How this works:</strong>
          </div>
          <p className="modal-info-text">
            Your feedback will create a GitHub issue that our team can review and act upon. 
            You can also contribute directly by submitting pull requests to improve the data.
          </p>
        </div>
      </div>
    </div>
  )
}

export default FeedbackModal